import { useRef, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import YupPassword from 'yup-password';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import ReCAPTCHA from 'react-google-recaptcha';
import { facebookPixelId } from 'context/trackingAnalytics/config';

import { FormInput, Info } from 'components';
import { RegisterRequest, TrackingEventsEnum } from 'types';
import { IRegistrationContext } from 'context';
import { useRegistration, useTrackingAnalytics } from 'hooks';
import LockIcon from '@mui/icons-material/Lock';

import styles from './RegisterForm.module.scss';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

const fbq = window.fbq;

YupPassword(Yup);

// ✅ Initialize Pixel only if this is the top-level page, and the pixel ID is present
if (window === window.parent && typeof fbq === 'function' && facebookPixelId) {
  fbq('init', facebookPixelId);
  fbq('track', 'PageView');
}

const schema = Yup.object().shape({
  firstName: Yup.string()
    .label('First Name')
    .trim()
    .matches(/^[a-zA-Z'-\s]*$/, 'Letters, spaces, apostrophes, or hyphens only.')
    .required('First Name is required.'),
  lastName: Yup.string()
    .label('Last Name')
    .trim()
    .matches(/^[a-zA-Z'-\s]*$/, 'Letters, spaces, apostrophes, or hyphens only.')
    .required('Last Name is required.'),
  email: Yup.string().label('Email').email('Email must be a valid').required(),
  organization: Yup.string().label('Organization').min(2).max(100, 'Max length is 100 characters').required(),
  password: Yup.string()
    .label('Password')
    .min(8, 'Must be at least 8 characters, one uppercase and lowercase letter, symbol, and number')
    .minLowercase(1, 'Password must contain at least 1 lowercase letter')
    .minUppercase(1, 'Password must contain at least 1 uppercase letter')
    .minNumbers(1, 'Password must contain at least 1 number')
    .minSymbols(1, 'Password must contain at least 1 special character')
    .required('Must be at least 8 characters, one uppercase and lowercase letter, symbol, and number'),
  passwordConfirmation: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match'),
  captcha: Yup.string().required(),
});

export function RegisterForm() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { onRegisterFormSubmit: onSubmit }: IRegistrationContext = useRegistration();

  const trackingAnalyticsContext = useTrackingAnalytics();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const location = useLocation();

  const [formStartTime, setFormStartTime] = useState<number | null>(null); // Track form start time

  const form = useForm<RegisterRequest>({
    resolver: yupResolver(schema),
    mode: 'all', // Fixes mobile first character skipped issue
    defaultValues: {},
  });

  useEffect(() => {
    const handleUserInput = () => {
      if (!formStartTime) {
        setFormStartTime(Date.now());
        console.log('Form start time recorded:', Date.now());
      }
    };

    const inputElements = document.querySelectorAll('input, textarea, select');

    inputElements.forEach((input) => {
      input.addEventListener('focus', handleUserInput);
      input.addEventListener('keydown', handleUserInput);
    });

    return () => {
      inputElements.forEach((input) => {
        input.removeEventListener('focus', handleUserInput);
        input.removeEventListener('keydown', handleUserInput);
      });
    };
  }, [formStartTime]);

  useEffect(() => {
    const isIframe = window !== window.parent;

    if (isIframe) {
      setTimeout(() => {
        const firstInput = document.querySelector('input, textarea') as HTMLInputElement | null;
        if (firstInput) {
          firstInput.focus();
          console.log('Re-focused first input to fix iframe typing bug');
        }
      }, 200);
    }
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    let isIframe = false;

    try {
      isIframe = window.self !== window.top;
    } catch (e) {
      isIframe = true;
    }

    const currentTime = Date.now();
    const trackingSource = isIframe ? 'iframe' : 'main-site';

    if (isIframe) {
      window.parent.postMessage({ fbEvent: 'CompleteRegistration' }, '*');
    }

    if (!formStartTime || currentTime - formStartTime < 10000) {
      enqueueSnackbar('Please take at least 10 seconds to fill out the form before submitting.', { variant: 'error' });
      console.warn('⏳ Submission blocked: Form completed too quickly.');
      setIsSubmitting(false);
      return;
    }

    if (!form.formState.isValid) {
      enqueueSnackbar('Please complete all required fields correctly.', { variant: 'error' });
      console.warn('⚠️ Form is invalid, please correct the errors.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (!onSubmit) {
        console.error('❌ Error: onSubmit function is undefined.');
        enqueueSnackbar('An internal error occurred. Please try again later.', { variant: 'error' });
        return;
      }

      await onSubmit(form.getValues());

      console.log(`✅ Submission successful from: ${trackingSource}`);
      trackingAnalyticsContext.trackEvent(TrackingEventsEnum.CompleteRegistration, {
        email: form.getValues().email,
        source: trackingSource,
      });

      enqueueSnackbar('Registration successful!', { variant: 'success' });
    } catch (error) {
      console.error('❌ Submission error:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'An error occurred during submission.', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCaptchaSuccess = (token: string | null) => {
    console.log('✅ reCAPTCHA Verified:', token);
    trackingAnalyticsContext.trackEvent(TrackingEventsEnum.Recaptcha_Success);

    // ✅ Set the captcha token in the form state
    form.setValue('captcha', token ?? '');
    form.trigger('captcha');
  };

  const onCaptchaError = (error: string) => {
    console.error('❌ reCAPTCHA Failed:', error);
    enqueueSnackbar('reCAPTCHA validation failed. Please try again.', { variant: 'error' });

    // ✅ Track reCAPTCHA failure event
    trackingAnalyticsContext.trackEvent(TrackingEventsEnum.Recaptcha_Failed, { error });

    // Reset reCAPTCHA to allow retry
    recaptchaRef.current?.reset();
  };

  const showHelp = (title: string, message: string) => {
    alert(`${title}\n\n${message}`);
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className={styles.form}>
      <div className={styles.rowWithTwoInputs}>
        <Stack className={styles.row}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography component='p' variant='body2'>
              First Name <span className={styles.asterisk}>*</span>
            </Typography>
          </Stack>
          <FormInput
            name='firstName'
            control={form.control}
            errorHelperClassName={styles.errorMessage}
            autoComplete='off'
          />
        </Stack>

        <Stack className={styles.row}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography component='p' variant='body2'>
              Last Name <span className={styles.asterisk}>*</span>
            </Typography>
          </Stack>
          <FormInput
            name='lastName'
            control={form.control}
            errorHelperClassName={styles.errorMessage}
            autoComplete='off'
          />
        </Stack>
      </div>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            Email <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput name='email' control={form.control} errorHelperClassName={styles.errorMessage} autoComplete='off' />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            Provider/Organization Name <span className={styles.asterisk}>*</span>
          </Typography>
          <Info text='Enter your organization name. If you are a sole practitioner, just enter your name.' />
        </Stack>
        <FormInput
          name='organization'
          control={form.control}
          errorHelperClassName={styles.errorMessage}
          autoComplete='off'
          placeholder='ex. Aaron Smith, LCSW or Practice, LLC'
          className={styles.inputField}
        />
      </Stack>

      <hr className={styles.divider} />

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            Create Password <span className={styles.asterisk}>*</span>
          </Typography>
          <Info text='Password must be at least 8 characters, one uppercase and lowercase letter, symbol, and number.' />
        </Stack>

        <FormInput
          name='password'
          control={form.control}
          errorHelperClassName={styles.errorMessage}
          type='password'
          disableError
        />

        {!form.watch('password') && !form.formState.errors.password && (
          <Typography className={styles.passwordRequirement}>
            Must be at least 8 characters, one uppercase and lowercase letter, symbol, and number.
          </Typography>
        )}

        {form.watch('password') && form.formState.errors.password && (
          <Typography className={styles.passwordError}>{form.formState.errors.password.message}</Typography>
        )}
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            Confirm Password <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput
          name='passwordConfirmation'
          control={form.control}
          errorHelperClassName={styles.errorMessage}
          autoComplete='off'
          type='password'
        />
      </Stack>

      <Stack className={styles.recaptchaContainer} alignItems='center'>
        <div
          onClick={() => {
            console.log('🛡️ reCAPTCHA Clicked');
            trackingAnalyticsContext.trackEvent(TrackingEventsEnum.Recaptcha_Clicked);
          }}
          className={styles.recaptchaWrapper}
        >
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ''}
            size='normal'
            ref={recaptchaRef}
            onChange={onCaptchaSuccess}
            onErrored={() => onCaptchaError('Error occurred while loading reCAPTCHA')}
            onExpired={() => onCaptchaError('reCAPTCHA expired, please try again')}
          />
        </div>
      </Stack>

      <Stack className={styles.row} alignItems='flex-end'>
        <Button
          id='RegisterFormNext'
          variant='contained'
          disabled={!form.formState.isValid || isSubmitting}
          className={styles.formButton}
          type='submit'
        >
          {isSubmitting ? 'Processing...' : 'Continue'}
        </Button>
      </Stack>

      <Stack direction='row' alignItems='center' spacing={1} className={styles.secureTextContainer}>
        <LockIcon className={styles.lockIcon} />
        <Typography variant='body2' className={styles.secureText}>
          This form is secure and encrypted to keep your data safe.
        </Typography>
      </Stack>
    </form>
  );
}

export default RegisterForm;
